<template>
    <div class="article-carousel">
        <div class="article-carousel__title">{{ $t('articles.whatsElseInColian') }}</div>
        <div v-if="loading" class="article-carousel__loading">
            <KLoader />
        </div>
        <div v-if="!loading && slides.length > 0" class="article-carousel__wrapper">
            <swiper
                ref="swiperRef"
                :modules="modules"
                :slides-per-view="'auto'"
                :space-between="0"
                :centered-slides="true"
                :loop="true"
                :effect="'coverflow'"
                :grab-cursor="true"
                :coverflow-effect="{
                    rotate: 50,
                    stretch: 0,
                    depth: 100,
                    modifier: 1,
                    slideShadows: true,
                  }"
                :breakpoints="{
                    320: {
                        slidesPerView: 1,
                        effect: 'coverflow',
                    },
                    992: {
                        slidesPerView: 3,
                        effect: 'coverflow',
                    },
                }"
            >
                <swiper-slide v-for="slide in slides" :key="slide.uuid" class="article-carousel__item-wrapper">
                    <div class="article-carousel__item-img-wrapper">
                        <img :src="slide.url" alt="kasanowa" @click="goToArticleDetails(slide.id)" />
                        <div class="article-carousel__arrow-right kasanowa-mobile" @click="nextSlide">
                            <KIcon icon="arrowRight" width="20" height="20" />
                        </div>
                        <div class="article-carousel__arrow-left kasanowa-mobile" @click="prevSlide">
                            <KIcon icon="arrowRight" width="20" height="20" />
                        </div>
                    </div>
                    <div class="article-carousel__item-title" @click="goToArticleDetails(slide.id)">{{ slide.title }}</div>
                    <div class="article-carousel__item-subtitle" @click="goToArticleDetails(slide.id)">{{ slide.lead }}</div>
                </swiper-slide>

                <div class="article-carousel__arrow-right kasanowa-desktop" @click="nextSlide">
                    <KIcon icon="arrowRight" width="20" height="20" />
                </div>
                <div class="article-carousel__arrow-left kasanowa-desktop" @click="prevSlide">
                    <KIcon icon="arrowRight" width="20" height="20" />
                </div>
            </swiper>
        </div>

        <div v-if="!loading && slides.length > 0" class="article-carousel__bottom-wrapper">
            <div class="article-carousel__bottom-text">{{ $t('articles.knowledgeSomething') }} <span>{{ $t('articles.new') }}</span></div>
            <KButton
                id="more-articles"
                :label="$t('articles.more')"
                red
                icon="arrowRight"
                @click="navigateTo('/articles-ph')"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/stores/user';
import { EffectCoverflow } from 'swiper/modules';
import ArticleApi from '@/api/v1/ArticleApi';
import { navigateTo } from '@/hooks/NavigateHooks';
import { toast } from 'vue3-toastify';
import type { IArticleResponse, IArticleSlide } from '@/models/ArticleModel';
import KButton from '@/components/atoms/KButton.vue';
import KIcon from '@/components/atoms/KIcon.vue';
import KLoader from '@/components/atoms/KLoader.vue';

import 'swiper/css';
import 'swiper/css/effect-coverflow';

const modules = [EffectCoverflow];
const swiperRef = ref<any>(null);

const slides = ref<IArticleSlide[]>([]);
const articleApi = new ArticleApi();
const loading = ref<boolean>(false);
const { isAdmin, isAdminColian } = storeToRefs(useUserStore());
const articleList = async() => {
    loading.value = true;
    try {
        const result = await articleApi.articleList(
            1,
            100,
            null,
            null,
            isAdmin.value || isAdminColian.value,
        );
        if (!result.isSuccess) {
            toast.error(t('articles.toast.errorArticleList'));
            loading.value = false;
            return;
        }
        const originalSlides = result.payload.data.map((elem: IArticleResponse) => ({
            id: elem.id,
            uuid: elem.id,
            title: elem.name,
            lead: elem.lead,
            url: elem.image,
        }));

        // Musze duplikowac arrayke poniewaz swiper sie wywala wtedy i nie widac wszystkich elementow a jak ma ich troche to juz dziala poprawnie, a dokladniej jak jest w arrayce conajmniej 5 elementow
        // jezeli jest tylko jeden element to musze zduplikowac 5 razy i tak dalej jak bedzie 2 elementy to 2 razy jak 3-5 to 1 raz, bo 1*5+1=6, 2*3=6, 3*2=6
        slides.value = originalSlides.length < 2
            ? [
                ...originalSlides,
                ...originalSlides.map(item => ({
                    ...item,
                    uuid: item.id + 1
                })),
                ...originalSlides.map(item => ({
                    ...item,
                    uuid: item.id + 2
                })),
                ...originalSlides.map(item => ({
                    ...item,
                    uuid: item.id + 3
                })),
                ...originalSlides.map(item => ({
                    ...item,
                    uuid: item.id + 4
                })),
                ...originalSlides.map(item => ({
                    ...item,
                    uuid: item.id + 5
                })),
            ]
            : originalSlides.length < 3
            ? [
                ...originalSlides,
                ...originalSlides.map(item => ({
                    ...item,
                    uuid: item.id + 1
                })),
                ...originalSlides.map(item => ({
                    ...item,
                    uuid: item.id + 2
                })),
               ]
            : originalSlides.length < 6
            ? [
                ...originalSlides,
                ...originalSlides.map(item => ({
                    ...item,
                    uuid: item.id + 1
                })),
               ]
            : originalSlides;
    } catch (e) {
        toast.error(t('articles.toast.errorArticleList'));
        loading.value = false;
        return;
    }
    loading.value = false;
};
articleList();

const nextSlide = () => {
    if (swiperRef.value && swiperRef.value.$el && swiperRef.value.$el.swiper) {
        swiperRef.value.$el.swiper.slideNext();
    }
};

const prevSlide = () => {
    if (swiperRef.value && swiperRef.value.$el && swiperRef.value.$el.swiper) {
        swiperRef.value.$el.swiper.slidePrev();
    }
};

const goToArticleDetails = (uuid: number) => {
    navigateTo(`/articles-ph/${uuid}`);
};
</script>

<style scoped lang="scss">
.article-carousel {
    position: relative;
    margin-top: 30px;

    @include media-breakpoint-up(lg) {
        margin-top: 150px;
    }

    @include media-breakpoint-up(xl) {
        margin-top: 100px;
    }

    &__title {
        background-color: $red-400;
        font-size: 26px;
        font-weight: 900;
        color: $white;
        text-align: center;
        text-transform: uppercase;
        padding: 8px;

        @include media-breakpoint-up(lg) {
            position: absolute;
            top: -110px;
            z-index: 2;
            left: 50%;
            transform: translateX(-50%);
            font-size: 42px;
            letter-spacing: 0;
            line-height: 1.2;
        }

        @include media-breakpoint-up(xl) {
            top: -60px;
            width: 310px;
        }
    }

    &__wrapper {
        position: relative;
        margin-top: 30px;
        cursor: pointer;

        &:deep(.swiper-slide-prev) {
            @include media-breakpoint-up(lg) {
                transform: translate3d(0, 0, -350px) rotateX(0deg) rotateY(0deg) scale(1.1) !important;
            }
        }

        &:deep(.swiper-slide-next) {
            @include media-breakpoint-up(lg) {
                transform: translate3d(0, 0, -350px) rotateX(0deg) rotateY(0deg) scale(1.1) !important;
            }
        }
    }

    &__item-wrapper {
        display: flex;
        flex-direction: column;
    }

    &__item-img-wrapper {
        width: 100%;
        height: 100%;
        position: relative;

        img {
            object-fit: cover;
            width: 99%;
            min-width: 99%;
            height: 300px;
            min-height: 300px;
            background-color: $primary-800;
            border: 1px solid $secondary-400;
            border-radius: 5px;

            @include media-breakpoint-up(sm) {
                height: 450px;
                min-height: 450px;
            }

            @include media-breakpoint-up(md) {
                height: 600px;
                min-height: 600px;
            }

            @include media-breakpoint-up(lg) {
                max-height: 433px;
                height: 260px;
                min-height: 260px;
            }

            @include media-breakpoint-up(xl) {
                height: 300px;
                min-height: 300px;
            }
        }
    }

    &__item-title {
        color: $white;
        font-weight: 700;
        font-size: 20px;
        text-align: center;
        text-transform: uppercase;
    }

    &__item-subtitle {
        color: $white;
        text-align: center;
    }

    &__arrow-right {
        position: absolute;
        z-index: 1;
        top: 50%;
        right: 10px;
        transform: translateY(-50%);
        width: 30px;
        height: 30px;
        display: flex;
        justify-content: center;
        align-items: center;
        border: 1px solid $secondary-400;
        border-radius: 50%;

        @include media-breakpoint-up(md) {
            width: 35px;
            height: 35px;
            right: 25px;
        }

        @include media-breakpoint-up(lg) {
            transform: translateY(-100%);
        }

        svg {
            fill: $secondary-400;
        }
    }

    &__arrow-left {
        position: absolute;
        z-index: 1;
        top: 50%;
        left: 10px;
        transform: translateY(-50%) rotate(180deg);
        width: 30px;
        height: 30px;
        display: flex;
        justify-content: center;
        align-items: center;
        border: 1px solid $red-400;
        border-radius: 50%;

        @include media-breakpoint-up(md) {
            width: 35px;
            height: 35px;
            left: 25px;
        }

        @include media-breakpoint-up(lg) {
            transform: translateY(-100%) rotate(180deg);
        }

        svg {
            fill: $red-400;
        }
    }

    &__bottom-wrapper {
        margin-top: 20px;
        display: flex;
        gap: 40px;
        justify-content: space-between;
        align-items: center;

        @include media-breakpoint-up(lg) {
            width: 33%;
            margin: 20px auto 0;
        }
    }

    &__bottom-text {
        color: $white;

        span {
            font-weight: 700;
            color: $red-400;
        }
    }

    &__loading {
        position: absolute;
        top: 80px;
        left: 50%;
        transform: translateX(-50%);
    }
}
</style>
